import { useIntl } from '@umijs/max';
import { Typography } from 'antd';
import React from 'react';
import { TouchSafeTooltip as Tooltip } from '../TouchSafeTooltip';

const { Text } = Typography;

type TextProps = React.ComponentProps<typeof Text>;

export interface CopyTextProps extends Omit<TextProps, 'copyable' | 'children'> {
  /** 要复制的值;省略时复制 children 的纯文本 */
  text?: string;
  /** 自定义提示 [复制前, 已复制];省略走默认文案 */
  tooltips?: [string, string];
  children: React.ReactNode;
}

/**
 * 点击文字即复制,无 icon。复制后 tooltip 短暂提示「已复制」。
 * 替代 antd `<Typography.Text copyable>`(去掉复制图标)。
 */
const CopyText: React.FC<CopyTextProps> = ({
  text,
  tooltips,
  children,
  onClick,
  style,
  ...rest
}) => {
  const intl = useIntl();
  const [copied, setCopied] = React.useState(false);
  const timer = React.useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  React.useEffect(() => () => clearTimeout(timer.current), []);

  const copyTip =
    tooltips?.[0] ??
    intl.formatMessage({ id: 'component.copyText.copy', defaultMessage: '点击复制' });
  const copiedTip =
    tooltips?.[1] ??
    intl.formatMessage({ id: 'component.copyText.copied', defaultMessage: '已复制' });

  const value = text ?? (typeof children === 'string' ? children : '');

  const handleCopy = async (e: React.MouseEvent<HTMLSpanElement>) => {
    onClick?.(e);
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = value;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
      } catch {
        /* noop */
      }
      document.body.removeChild(ta);
    }
    setCopied(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Tooltip title={copied ? copiedTip : copyTip}>
      <Text {...rest} onClick={handleCopy} style={{ cursor: 'pointer', ...style }}>
        {children}
      </Text>
    </Tooltip>
  );
};

export default CopyText;
